/* eslint-disable react-refresh/only-export-components -- compound-component namespace pattern: the 6 wrapper functions are bundled into a Menu object export; splitting into 6 separate files adds unnecessary churn for trivial wrappers with no independent lifecycle. */
import { type ComponentProps as IMenuPartProps } from 'react';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import styles from './styles.module.css';

type ClassNameProp<S> = string | ((state: S) => string | undefined) | undefined;

function resolveClassName<S>(className: ClassNameProp<S>, state: S): string | undefined {
  return typeof className === 'function' ? className(state) : className;
}

function cx(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(' ');
}

function Trigger({ className, ...rest }: IMenuPartProps<typeof BaseMenu.Trigger>) {
  return (
    <BaseMenu.Trigger
      {...rest}
      className={(state) => cx(styles.trigger, resolveClassName(className, state))}
    />
  );
}

function Positioner({ className, ...rest }: IMenuPartProps<typeof BaseMenu.Positioner>) {
  return (
    <BaseMenu.Positioner
      {...rest}
      className={(state) => cx(styles.positioner, resolveClassName(className, state))}
    />
  );
}

function Popup({ className, ...rest }: IMenuPartProps<typeof BaseMenu.Popup>) {
  return (
    <BaseMenu.Popup
      {...rest}
      className={(state) => cx(styles.popup, resolveClassName(className, state))}
    />
  );
}

function Item({ className, ...rest }: IMenuPartProps<typeof BaseMenu.Item>) {
  return (
    <BaseMenu.Item
      {...rest}
      className={(state) => cx(styles.item, resolveClassName(className, state))}
    />
  );
}

function SubmenuTrigger({ className, ...rest }: IMenuPartProps<typeof BaseMenu.SubmenuTrigger>) {
  return (
    <BaseMenu.SubmenuTrigger
      {...rest}
      className={(state) => cx(styles.item, resolveClassName(className, state))}
    />
  );
}

/** Toggleable menu entry (`role="menuitemcheckbox"`) — pair with
 *  `closeOnClick={false}` so flipping several options keeps the menu open. */
function CheckboxItem({ className, ...rest }: IMenuPartProps<typeof BaseMenu.CheckboxItem>) {
  return (
    <BaseMenu.CheckboxItem
      {...rest}
      className={(state) => cx(styles.item, resolveClassName(className, state))}
    />
  );
}

function Separator({ className, ...rest }: IMenuPartProps<typeof BaseMenu.Separator>) {
  return (
    <BaseMenu.Separator
      {...rest}
      className={(state) => cx(styles.separator, resolveClassName(className, state))}
    />
  );
}

export const Menu = {
  Root: BaseMenu.Root,
  Trigger,
  Portal: BaseMenu.Portal,
  Positioner,
  Popup,
  Item,
  CheckboxItem,
  SubmenuRoot: BaseMenu.SubmenuRoot,
  SubmenuTrigger,
  Separator,
};
